import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import styles from "./SmtpSetup.module.css";
import TextfieldInput from "@_components/TextfieldInput/TextfieldInput";
import { emailSetupActions } from "@_actions/email_setup.actions";
import ValidateEmailSetupPopup from "@_components/popups/ValidateEmailSetupPopup/ValidateEmailSetupPopup";
import { checkGoogleServer, getUserDataFromJwt } from "@_utils/helpers";
import { isNull, catchError } from "@_utils/helpers";
import CheckIcon from "@mui/icons-material/Check";

import makeStyles from "@mui/styles/makeStyles";
import { Button, Menu, MenuItem } from "@mui/material";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import InboundEmailSetup from "@_components/InboundEmailSetup/InboundEmailSetup";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const useStyles = makeStyles({
  root: {
    paddingLeft: 0,
  },
});

const defaultSmtpDetails = {
  smtp_host: "",
  smtp_port: "",
  smtp_email: "",
  smtp_password: "",
  smtp_username: "",
  email_sender_name: "",
  default: true,
};

function isDefaultSmtpDetails(details) {
  return JSON.stringify(details) === JSON.stringify(defaultSmtpDetails);
}

function getSmtpProviderFromSetup(emailSetup) {
  if (!emailSetup) return "Choose";
  const hasSmtpCredentials =
    emailSetup.smtp_host && String(emailSetup.smtp_host).trim() !== "";
  if (emailSetup.oauth_provider) return emailSetup.oauth_provider;
  if (emailSetup.active_email_setup === "SMTP") return "Other";
  // Persisted SMTP host but active_email_setup not yet set (e.g. API lag after save)
  if (hasSmtpCredentials) return "Other";
  return "Choose";
}

/** Server has basic SMTP fields saved (used with active_email_setup for icons) */
function isServerBasicSmtpConfigured(emailSetup) {
  if (!emailSetup || emailSetup.oauth_provider) return false;
  if (emailSetup.active_email_setup === "SMTP") return true;
  return !!(emailSetup.smtp_host && String(emailSetup.smtp_host).trim() !== "");
}

function SmtpSetup(props) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const onError = props.onError;

  const jwtData = getUserDataFromJwt();

  const emailSetupReducer = useSelector((state) => state.emailSetupReducer);

  const [openValidatePopup, setOpenValidatePopup] = useState(false);
  const [isGoogle, setIsGoogle] = useState(false);

  /**
   * State hook for managing the currently chosen SMTP provider.
   * Initializes with the OAuth provider from email setup, or "Other" if SMTP is active,
   * or "Choose" as default.
   *
   * @type {[string, function]} Array containing the current SMTP provider value and its setter function
   * @description The state can be:
   * - OAuth provider name if available in email setup
   * - "Other" if active email setup is SMTP but no OAuth provider is set
   * - "Choose" as fallback default value
   */
  const [currentChoosenSmtpProvider, setCurrentChoosenSmtpProvider] = useState(
    () => getSmtpProviderFromSetup(emailSetupReducer.email_setup),
  );

  const [openMenu, setOpenMenu] = useState(null);

  const [smtpDetails, setSmtpDetails] = useState({
    smtp_host: !emailSetupReducer.email_setup.oauth_provider
      ? emailSetupReducer.email_setup.smtp_host || ""
      : "",
    smtp_port: !emailSetupReducer.email_setup.oauth_provider
      ? emailSetupReducer.email_setup.smtp_port || ""
      : "",
    smtp_email: !emailSetupReducer.email_setup.oauth_provider
      ? emailSetupReducer.email_setup.smtp_email || ""
      : "",
    smtp_password: !emailSetupReducer.email_setup.oauth_provider
      ? emailSetupReducer.email_setup.smtp_password || ""
      : "",
    smtp_username: !emailSetupReducer.email_setup.oauth_provider
      ? emailSetupReducer.email_setup.smtp_username ===
        emailSetupReducer.email_setup.smtp_email
        ? ""
        : emailSetupReducer.email_setup.smtp_username || ""
      : "",
    email_sender_name: emailSetupReducer.email_setup.email_sender_name || "",
  });

  const [isClicked, setIsClicked] = useState(false);
  const [updateEmailSenderNameInProcess, setUpdateEmailSenderNameInProcess] =
    useState(false);

  const [checkbox, setCheckbox] = useState(false);
  const { snackbarProps, showSnackbarError } = useSnackbar();

  // load email setup to get smtp details
  useEffect(() => {
    dispatch(emailSetupActions.getEmailSetup());
  }, [dispatch]);

  // Sync form from GET response. Depends only on EMAIL_SETUP_GET so other status changes
  // (e.g. validation) do not re-run this and reset the provider dropdown by mistake.
  useEffect(() => {
    if (emailSetupReducer.status.EMAIL_SETUP_GET === "success") {
      setSmtpDetails({
        smtp_host: !emailSetupReducer.email_setup.oauth_provider
          ? emailSetupReducer.email_setup.smtp_host || ""
          : "",
        smtp_port: !emailSetupReducer.email_setup.oauth_provider
          ? emailSetupReducer.email_setup.smtp_port || ""
          : "",
        smtp_email: !emailSetupReducer.email_setup.oauth_provider
          ? emailSetupReducer.email_setup.smtp_email || ""
          : "",
        smtp_password: !emailSetupReducer.email_setup.oauth_provider
          ? emailSetupReducer.email_setup.smtp_password || ""
          : "",
        smtp_username: !emailSetupReducer.email_setup.oauth_provider
          ? emailSetupReducer.email_setup.smtp_username ===
            emailSetupReducer.email_setup.smtp_email
            ? ""
            : emailSetupReducer.email_setup.smtp_username || ""
          : "",
        email_sender_name:
          emailSetupReducer.email_setup.email_sender_name || "",
      });
      setCurrentChoosenSmtpProvider(
        getSmtpProviderFromSetup(emailSetupReducer.email_setup),
      );
      dispatch(emailSetupActions.clearStatusMessage("EMAIL_SETUP_GET"));
    } else if (catchError(emailSetupReducer.status.EMAIL_SETUP_GET)) {
      showSnackbarError(emailSetupReducer.status.EMAIL_SETUP_GET);
      dispatch(emailSetupActions.clearStatusMessage("EMAIL_SETUP_GET"));
    } else return;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to GET completing, not validation/other status
  }, [dispatch, emailSetupReducer.status.EMAIL_SETUP_GET]);

  useEffect(() => {
    if (
      smtpDetails.smtp_username &&
      smtpDetails.smtp_email &&
      smtpDetails.smtp_username !== smtpDetails.smtp_email
    ) {
      setCheckbox(true);
    }
  }, [smtpDetails.smtp_username, smtpDetails.smtp_email]);

  useEffect(() => {
    if (checkGoogleServer(smtpDetails.smtp_host)) setIsGoogle(true);
    else setIsGoogle(false);
  }, [smtpDetails.smtp_host]);

  /*After a successful SMTP save: clear the form and refetch. Depends only on
  EMAIL_SETUP_PUT and not smtpDetails, so typing does not re-run this while success is still set.*/
  useEffect(() => {
    if (emailSetupReducer.status.EMAIL_SETUP_PUT === "success") {
      dispatch(emailSetupActions.clearStatusMessage("EMAIL_SETUP_PUT"));
      setSmtpDetails(defaultSmtpDetails);
      dispatch(emailSetupActions.getEmailSetup());
    }
  }, [dispatch, emailSetupReducer.status.EMAIL_SETUP_PUT]);

  const handleInputChange = (e) => {
    let { name, value } = e.target;

    if (name) name = name.replace("_oauth", ""); // to handle email_sender_name_oauth field
    if (name === "smtp_host" && checkGoogleServer(value)) setIsGoogle(true);
    else setIsGoogle(false);
    setSmtpDetails((smtpDetails) => ({
      ...smtpDetails,
      [name]: value,
    }));
  };

  const handleOpenMenu = (event) => {
    setOpenMenu(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenu(null);
  };

  const handleGmailOption = () => {
    setCurrentChoosenSmtpProvider("GMAIL");
    setOpenMenu(null);
  };

  const handleOutlookOption = () => {
    setCurrentChoosenSmtpProvider("OUTLOOK");
    setOpenMenu(null);
  };

  const handleOtherOption = () => {
    setCurrentChoosenSmtpProvider("Other");
    setOpenMenu(null);
  };

  const handleUpdateEmailSetup = (activate = false) => {
    setIsClicked(true);

    if (!smtpDetails.smtp_username && !checkbox) {
      let data = {
        ...smtpDetails,
        smtp_port: parseInt(smtpDetails.smtp_port),
        smtp_username: smtpDetails.smtp_email,
      };
      dispatch(emailSetupActions.updateOutboundEmailSetup(data));
    } else {
      let data = {
        ...smtpDetails,
        smtp_port: parseInt(smtpDetails.smtp_port),
      };
      dispatch(emailSetupActions.updateOutboundEmailSetup(data));
    }

    let activationData = {
      active_email_setup: "SMTP",
    };

    setTimeout(() => {
      if (activate) {
        dispatch(emailSetupActions.activateOutboundEmailSetup(activationData));
      }
    }, 400);
  };

  const handleUpdateEmailSenderName = () => {
    setIsClicked(true);
    if (!smtpDetails.email_sender_name) {
      showSnackbarError("Please enter an email sender name.");
      return;
    }

    let data = {
      email_sender_name: smtpDetails.email_sender_name,
    };

    dispatch(emailSetupActions.updateEmailSenderName(data));
  };

  useEffect(() => {
    if (emailSetupReducer.status.EMAIL_SENDER_NAME_PUT === "request") {
      setUpdateEmailSenderNameInProcess(true);
      dispatch(emailSetupActions.clearStatusMessage("EMAIL_SENDER_NAME_PUT"));
    }

    if (emailSetupReducer.status.EMAIL_SENDER_NAME_PUT === "success") {
      setUpdateEmailSenderNameInProcess(false);
      dispatch(emailSetupActions.clearStatusMessage("EMAIL_SENDER_NAME_PUT"));
      setIsClicked(false);
      dispatch(emailSetupActions.getEmailSetup());
      setUpdateEmailSenderNameInProcess(false);
    }

    if (catchError(emailSetupReducer.status.EMAIL_SENDER_NAME_PUT)) {
      setUpdateEmailSenderNameInProcess(false);
      dispatch(emailSetupActions.clearStatusMessage("EMAIL_SENDER_NAME_PUT"));
    }
  }, [emailSetupReducer.status.EMAIL_SENDER_NAME_PUT, dispatch]);

  const handleValidateSetup = (e) => {
    setIsClicked(true);

    if (!smtpDetails.smtp_username && !checkbox) {
      let data = {
        ...smtpDetails,
        smtp_port: parseInt(smtpDetails.smtp_port),
        smtp_username: smtpDetails.smtp_email,
      };
      dispatch(emailSetupActions.validateOutboundEmailSetup(data, "smtp"));
    } else {
      let data = {
        ...smtpDetails,
        smtp_port: parseInt(smtpDetails.smtp_port),
      };
      dispatch(emailSetupActions.validateOutboundEmailSetup(data, "smtp"));
    }
    setOpenValidatePopup(true);
  };

  const handleConnectOauth = () => {
    dispatch(
      emailSetupActions.connectOauth(
        currentChoosenSmtpProvider,
        smtpDetails.smtp_email,
      ),
    );
  };

  // Handle OAuth connection effect
  useEffect(() => {
    if (emailSetupReducer.status.EMAIL_OAUTH_CONNECT_POST === "success") {
      dispatch(
        emailSetupActions.clearStatusMessage("EMAIL_OAUTH_CONNECT_POST"),
      );

      window.location.href = emailSetupReducer.email_setup.oauth_url;
      return;
    }

    if (catchError(emailSetupReducer.status.EMAIL_OAUTH_CONNECT_POST)) {
      dispatch(
        emailSetupActions.clearStatusMessage("EMAIL_OAUTH_CONNECT_POST"),
      );
      if (onError)
        onError("Failed to initialize Authentication. Please try again.");
      else {
        showSnackbarError("Failed to initialize Authentication. Please try again.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    emailSetupReducer.status.EMAIL_OAUTH_CONNECT_POST,
    dispatch,
    onError,
    showSnackbarError,
  ]);

  const handleDisconnectOauth = () => {
    dispatch(emailSetupActions.disconnectOauth(currentChoosenSmtpProvider));
  };

  // Handle OAuth disconnection effect
  useEffect(() => {
    if (emailSetupReducer.status.EMAIL_OAUTH_DISCONNECT_DELETE === "success") {
      dispatch(
        emailSetupActions.clearStatusMessage("EMAIL_OAUTH_DISCONNECT_DELETE"),
      );
      setSmtpDetails(defaultSmtpDetails);
      dispatch(emailSetupActions.getEmailSetup());
      return;
    }

    if (catchError(emailSetupReducer.status.EMAIL_OAUTH_DISCONNECT_DELETE)) {
      dispatch(
        emailSetupActions.clearStatusMessage("EMAIL_OAUTH_DISCONNECT_DELETE"),
      );
      if (onError)
        onError("Failed to disconnect your account. Please try again.");
      else {
        showSnackbarError("Failed to disconnect your account. Please try again.");
      }
    }
  }, [
    emailSetupReducer.status.EMAIL_OAUTH_DISCONNECT_DELETE,
    dispatch,
    onError,
    showSnackbarError,
  ]);

  // Handle Email Setup activation effect
  useEffect(() => {
    if (emailSetupReducer.status.EMAIL_SETUP_ACTIVATION_PUT === "success") {
      dispatch(
        emailSetupActions.clearStatusMessage("EMAIL_SETUP_ACTIVATION_PUT"),
      );
      setSmtpDetails(defaultSmtpDetails);
      dispatch(emailSetupActions.getEmailSetup());
      return;
    }

    if (catchError(emailSetupReducer.status.EMAIL_SETUP_ACTIVATION_PUT)) {
      dispatch(
        emailSetupActions.clearStatusMessage("EMAIL_SETUP_ACTIVATION_PUT"),
      );
      if (onError)
        onError("Failed to activate your account. Please try again.");
      else {
        showSnackbarError("Failed to activate your account. Please try again.");
      }
    }
  }, [
    emailSetupReducer.status.EMAIL_SETUP_ACTIVATION_PUT,
    dispatch,
    onError,
    showSnackbarError,
  ]);

  // Handle reload of email setup
  useEffect(() => {
    if (
      emailSetupReducer &&
      emailSetupReducer.email_setup &&
      isDefaultSmtpDetails(smtpDetails)
    ) {
      setSmtpDetails({
        smtp_host: !emailSetupReducer.email_setup.oauth_provider
          ? emailSetupReducer.email_setup.smtp_host || ""
          : "",
        smtp_port: !emailSetupReducer.email_setup.oauth_provider
          ? emailSetupReducer.email_setup.smtp_port || ""
          : "",
        smtp_email: !emailSetupReducer.email_setup.oauth_provider
          ? emailSetupReducer.email_setup.smtp_email || ""
          : "",
        smtp_password: !emailSetupReducer.email_setup.oauth_provider
          ? emailSetupReducer.email_setup.smtp_password || ""
          : "",
        smtp_username: !emailSetupReducer.email_setup.oauth_provider
          ? emailSetupReducer.email_setup.smtp_username ===
            emailSetupReducer.email_setup.smtp_email
            ? ""
            : emailSetupReducer.email_setup.smtp_username || ""
          : "",
        email_sender_name:
          emailSetupReducer.email_setup.email_sender_name || "",
      });
    }
    // we wont add smtpDetails as dependency to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSetupReducer.email_setup, emailSetupReducer]);

  const printCurrentProvider = () => {
    return (
      <>
        {(emailSetupReducer.email_setup.oauth_provider === "GMAIL" ||
          emailSetupReducer.email_setup.oauth_provider === "OUTLOOK") && (
          <>
            <p>
              You are connected with your{" "}
              {emailSetupReducer.email_setup.oauth_provider === "GMAIL"
                ? "Gmail"
                : "Outlook"}{" "}
              account <b>{emailSetupReducer.email_setup.smtp_email}</b>
            </p>
          </>
        )}
        {emailSetupReducer.email_setup.active_email_setup === "SMTP" && (
          <p>
            You are connected with account{" "}
            <b>{emailSetupReducer.email_setup.smtp_email}</b> (basic
            authentication)
          </p>
        )}
        {!emailSetupReducer.email_setup.active_email_setup && (
          <p>Select your email provider to connect your account.</p>
        )}
      </>
    );
  };

  const emailSetup = emailSetupReducer.email_setup;
  const showOAuthProviderCheck =
    currentChoosenSmtpProvider === emailSetup.oauth_provider &&
    currentChoosenSmtpProvider !== "Choose";
  // "Other (Basic)" provider: icons from GET — active_email_setup === "SMTP" vs null / not active
  const basicSmtpPresentOnServer = isServerBasicSmtpConfigured(emailSetup);
  const showBasicSmtpCheckIcon =
    currentChoosenSmtpProvider === "Other" && basicSmtpPresentOnServer;

  return (
    <div>
      <AppSnackbar {...snackbarProps} />
      <div className={styles.textContainer}>
        <h2>Outbound emails</h2>
        <div className={styles.smtp_text}>
          {printCurrentProvider()}

          <div className={styles.basic_smtp_inputs_container}>
            <Button
              variant="outlined"
              color="default"
              className={styles.btn_select_email_provider}
              onClick={handleOpenMenu}
              endIcon={<ArrowDropDownIcon />}
            >
              {showOAuthProviderCheck || showBasicSmtpCheckIcon ? (
                <div className={styles.menu_item}>
                  <p>{currentChoosenSmtpProvider}</p>{" "}
                  <CheckIcon className={styles.check_icon} />
                </div>
              ) : (
                <div className={styles.menu_item}>
                  {" "}
                  <p>{currentChoosenSmtpProvider}</p>
                </div>
              )}
            </Button>
            <Menu
              id="provider_dropdown_menu"
              anchorEl={openMenu}
              keepMounted={false}
              open={Boolean(openMenu)}
              onClose={handleCloseMenu}
            >
              <MenuItem
                className={styles.provider_dropdown_item}
                onClick={() => handleGmailOption()}
              >
                <div className={styles.menu_item}>
                  <p>Gmail (OAuth2)</p>{" "}
                  {emailSetupReducer.email_setup.oauth_provider === "GMAIL" && (
                    <CheckIcon className={styles.check_icon} />
                  )}
                </div>
              </MenuItem>
              <MenuItem
                className={styles.provider_dropdown_item}
                onClick={() => handleOutlookOption()}
              >
                <div className={styles.menu_item}>
                  <p>Outlook (OAuth2)</p>{" "}
                  {emailSetupReducer.email_setup.oauth_provider ===
                    "OUTLOOK" && (
                    <>
                      <CheckIcon className={styles.check_icon} />
                    </>
                  )}
                </div>
              </MenuItem>
              <MenuItem
                className={styles.provider_dropdown_item}
                onClick={() => handleOtherOption()}
              >
                <div className={styles.menu_item}>
                  <p>Other (Basic)</p>{" "}
                  {showBasicSmtpCheckIcon && (
                    <CheckIcon className={styles.check_icon} />
                  )}
                </div>
              </MenuItem>
            </Menu>
          </div>
        </div>

        {currentChoosenSmtpProvider !== "Choose" && (
          <>
            {currentChoosenSmtpProvider === "GMAIL" ||
            currentChoosenSmtpProvider === "OUTLOOK" ? (
              <>
                {currentChoosenSmtpProvider ===
                  emailSetupReducer.email_setup.oauth_provider &&
                emailSetupReducer.email_setup.oauth_connected ? (
                  <div className={styles.btn_container_connect}>
                    <Button
                      className={styles.save_disconnect_btn_oauth}
                      onClick={() => handleDisconnectOauth()}
                    >
                      Disconnect
                    </Button>
                    <div
                      style={{
                        width: "100%",
                        height: "5px",
                        backgroundColor: "#E0E0E0",
                        margin: "20px 0",
                      }}
                    >
                      <hr />
                    </div>
                  </div>
                ) : (
                  <div className={styles.btn_container_connect}>
                    <Button
                      className={styles.save_activate_btn}
                      onClick={() => handleConnectOauth()}
                    >
                      Connect
                    </Button>
                    <div className={styles.gray_bar_devider}>
                      <hr />
                    </div>
                  </div>
                )}
                <div className={styles.connected_account_container}>
                  <div>
                    <TextfieldInput
                      id={"email_sender_name_oauth"}
                      name={"email_sender_name_oauth"}
                      value={smtpDetails.email_sender_name}
                      onChange={handleInputChange}
                      required
                      error={isClicked && isNull(smtpDetails.email_sender_name)}
                      label="Email sender name"
                      className={styles.email_sender_name}
                    />
                  </div>
                  <Button
                    className={styles.save_emailname_btn_oauth}
                    onClick={() => handleUpdateEmailSenderName()}
                    disabled={updateEmailSenderNameInProcess}
                  >
                    Save
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div>
                  {isGoogle && (
                    <p>
                      Please make sure to setup the following requirement:{" "}
                      <a
                        className={styles.link}
                        href="https://support.google.com/accounts/answer/6010255?hl=en#zippy="
                      >
                        Google account
                      </a>
                    </p>
                  )}
                  <div className={styles.input_row_vertically_spaced}>
                    <div style={{ marginTop: 25 }}>
                      <div className={styles.input_container}>
                        <TextfieldInput
                          id={"smtp_email"}
                          name={"smtp_email"}
                          value={smtpDetails.smtp_email}
                          onChange={handleInputChange}
                          required
                          margin="none"
                          error={isClicked && isNull(smtpDetails.smtp_email)}
                          label="Email"
                        />
                      </div>
                    </div>
                    <div className={styles.checkbox_container}>
                      <Checkbox
                        checked={checkbox}
                        onChange={() => {
                          setCheckbox(!checkbox);
                          setSmtpDetails({
                            ...smtpDetails,
                            smtp_username: "",
                          });
                        }}
                        className={classes.root}
                      />
                      <p>The smtp username differs from the email address</p>
                    </div>
                    {checkbox && (
                      <div>
                        <div className={styles.input_container}>
                          <TextfieldInput
                            id={"smtp_username"}
                            name={"smtp_username"}
                            value={smtpDetails.smtp_username}
                            onChange={handleInputChange}
                            required
                            error={
                              isClicked && isNull(smtpDetails.smtp_username)
                            }
                            label="Username"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={styles.input_row_2_columns}>
                    <div>
                      <div className={styles.input_container}>
                        {" "}
                        <TextfieldInput
                          id={"smtp_password"}
                          name={"smtp_password"}
                          value={smtpDetails.smtp_password}
                          onChange={handleInputChange}
                          required
                          error={isClicked && isNull(smtpDetails.smtp_password)}
                          type="password"
                          label="Password"
                        />
                      </div>
                    </div>
                    <div
                      style={{ visibility: "hidden" }}
                      className={styles.btn_container}
                    >
                      {
                        // this is a dummy element to align the password input with the save button of the other column, it will be invisible but still take the space
                      }
                      <div style={{ width: "174px", height: "30px" }}>
                        <p></p>
                      </div>
                    </div>

                    <div>
                      <div className={styles.input_container}>
                        <TextfieldInput
                          id={"smtp_host"}
                          name={"smtp_host"}
                          value={smtpDetails.smtp_host}
                          onChange={handleInputChange}
                          required
                          error={isClicked && isNull(smtpDetails.smtp_host)}
                          label="SMTP host"
                        />
                      </div>
                    </div>

                    {emailSetupReducer.status.EMAIL_SETUP_VALIDATION_PUT ===
                      "request" ||
                    emailSetupReducer.status.EMAIL_SETUP_ACTIVATION_PUT ===
                      "request" ||
                    emailSetupReducer.status.EMAIL_SETUP_PUT === "request" ? (
                      <div className={styles.circular_progress_container}>
                        <CircularProgress />
                      </div>
                    ) : (
                      <>
                        <div className={styles.btn_container_validate}>
                          <Button
                            className={styles.validate_btn} // TODO: disable when infos is missing
                            onClick={() => handleValidateSetup()}
                          >
                            validate my setup
                          </Button>
                        </div>
                      </>
                    )}

                    <div>
                      <div className={styles.input_container}>
                        {" "}
                        <TextfieldInput
                          id={"smtp_port"}
                          name={"smtp_port"}
                          value={smtpDetails.smtp_port}
                          onChange={handleInputChange}
                          required
                          error={isClicked && isNull(smtpDetails.smtp_port)}
                          label="SMTP port"
                        />
                      </div>
                    </div>

                    {emailSetupReducer.email_setup &&
                    emailSetupReducer.email_setup.active_email_setup !==
                      "SMTP" ? (
                      <div className={styles.btn_container}>
                        <Button
                          className={styles.save_activate_btn}
                          onClick={() => handleUpdateEmailSetup(true)}
                        >
                          Save & Activate
                        </Button>
                      </div>
                    ) : (
                      <div className={styles.btn_container}>
                        <Button
                          className={styles.save_activate_btn}
                          onClick={() => handleUpdateEmailSetup()}
                        >
                          Save
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className={styles.gray_bar_devider}>
                    <hr />
                  </div>
                  <div className={styles.input_row_2_columns}>
                    <div className={styles.input_container}>
                      <TextfieldInput
                        id={"email_sender_name"}
                        name={"email_sender_name"}
                        value={smtpDetails.email_sender_name}
                        onChange={handleInputChange}
                        required
                        error={
                          isClicked && isNull(smtpDetails.email_sender_name)
                        }
                        label="Email sender name"
                        className={styles.email_sender_name}
                      />
                    </div>
                    <Button
                      className={styles.save_emailname_btn_oauth}
                      onClick={() => handleUpdateEmailSenderName()}
                      disabled={updateEmailSenderNameInProcess}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </>
            )}
            <InboundEmailSetup />

            <ValidateEmailSetupPopup
              open={openValidatePopup}
              close={() => setOpenValidatePopup(false)}
              id={jwtData.customer_id}
              smtpData={smtpDetails}
              checkbox={checkbox}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default SmtpSetup;
